import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import {
  Shield, Users, Building2, Megaphone, DollarSign, LogOut, Search,
  Trash2, Edit, Eye, ChevronLeft, ChevronRight, MapPin, X, Check,
  TrendingUp, Activity, Crown, UserCog, BarChart3, FileText
} from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const SuperAdminDashboard = () => {
  const { language } = useLanguage();
  const navigate = useNavigate();
  const t = (fr, en) => language === 'fr' ? fr : en;

  const [activeTab, setActiveTab] = useState('overview');
  const [stats, setStats] = useState(null);
  const [members, setMembers] = useState([]);
  const [membersMeta, setMembersMeta] = useState({ total: 0, page: 1, pages: 1 });
  const [listings, setListings] = useState([]);
  const [listingsMeta, setListingsMeta] = useState({ total: 0, page: 1, pages: 1 });
  const [businesses, setBusinesses] = useState([]);
  const [businessesMeta, setBusinessesMeta] = useState({ total: 0, page: 1, pages: 1 });
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [selectedMember, setSelectedMember] = useState(null);
  const [editModal, setEditModal] = useState(null);
  const [adminUser, setAdminUser] = useState(null);

  const getToken = () => localStorage.getItem('super-admin-token');
  const headers = () => ({ Authorization: `Bearer ${getToken()}` });

  // Check auth
  useEffect(() => {
    const token = getToken();
    const user = localStorage.getItem('super-admin-user');
    if (!token || !user) {
      navigate('/super-admin');
      return;
    }
    setAdminUser(JSON.parse(user));
  }, [navigate]);

  const fetchStats = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/super-admin/dashboard/stats`, { headers: headers() });
      setStats(res.data);
    } catch (err) {
      if (err.response?.status === 401 || err.response?.status === 403) {
        localStorage.removeItem('super-admin-token');
        localStorage.removeItem('super-admin-user');
        navigate('/super-admin');
      }
      console.error('Stats fetch error:', err.message);
    }
  }, [navigate]);

  const fetchMembers = useCallback(async (page = 1, search = '') => {
    try {
      const params = new URLSearchParams({ page, limit: 25 });
      if (search) params.append('search', search);
      const res = await axios.get(`${API}/api/super-admin/members?${params}`, { headers: headers() });
      setMembers(res.data.members);
      setMembersMeta({ total: res.data.total, page: res.data.page, pages: res.data.pages });
    } catch (err) { console.error('Members fetch error:', err.message); }
  }, []);

  const fetchListings = useCallback(async (page = 1, search = '') => {
    try {
      const params = new URLSearchParams({ page, limit: 25 });
      if (search) params.append('search', search);
      const res = await axios.get(`${API}/api/super-admin/listings?${params}`, { headers: headers() });
      setListings(res.data.listings);
      setListingsMeta({ total: res.data.total, page: res.data.page, pages: res.data.pages });
    } catch (err) { console.error('Listings fetch error:', err.message); }
  }, []);

  const fetchBusinesses = useCallback(async (page = 1, search = '') => {
    try {
      const params = new URLSearchParams({ page, limit: 25 });
      if (search) params.append('search', search);
      const res = await axios.get(`${API}/api/super-admin/businesses?${params}`, { headers: headers() });
      setBusinesses(res.data.businesses);
      setBusinessesMeta({ total: res.data.total, page: res.data.page, pages: res.data.pages });
    } catch (err) { console.error('Businesses fetch error:', err.message); }
  }, []);

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      await fetchStats();
      setLoading(false);
    };
    if (getToken()) init();
  }, [fetchStats]);

  useEffect(() => {
    if (!getToken()) return;
    if (activeTab === 'members') fetchMembers(1, '');
    else if (activeTab === 'listings') fetchListings(1, '');
    else if (activeTab === 'businesses') fetchBusinesses(1, '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const handleSearch = () => {
    if (activeTab === 'members') fetchMembers(1, searchQuery);
    else if (activeTab === 'listings') fetchListings(1, searchQuery);
    else if (activeTab === 'businesses') fetchBusinesses(1, searchQuery);
  };

  const handleDeleteMember = async (userId, name) => {
    if (!window.confirm(t(`Supprimer ${name} et toutes ses données ?`, `Delete ${name} and all associated data?`))) return;
    try {
      await axios.delete(`${API}/api/super-admin/members/${userId}`, { headers: headers() });
      fetchMembers(membersMeta.page, searchQuery);
      fetchStats();
    } catch (err) { console.error('Delete member error:', err.message); }
  };

  const handleDeleteListing = async (listingId) => {
    if (!window.confirm(t('Supprimer cette annonce ?', 'Delete this listing?'))) return;
    try {
      await axios.delete(`${API}/api/super-admin/listings/${listingId}`, { headers: headers() });
      fetchListings(listingsMeta.page, searchQuery);
    } catch (err) { console.error('Delete listing error:', err.message); }
  };

  const handleDeleteBusiness = async (businessId) => {
    if (!window.confirm(t('Supprimer cette entreprise ?', 'Delete this business?'))) return;
    try {
      await axios.delete(`${API}/api/super-admin/businesses/${businessId}`, { headers: headers() });
      fetchBusinesses(businessesMeta.page, searchQuery);
    } catch (err) { console.error('Delete business error:', err.message); }
  };

  const handleUpdateMember = async (userId, data) => {
    try {
      await axios.put(`${API}/api/super-admin/members/${userId}`, data, { headers: headers() });
      setEditModal(null);
      fetchMembers(membersMeta.page, searchQuery);
    } catch (err) { console.error('Update member error:', err.message); }
  };

  const handleUpdateBusinessStatus = async (businessId, status) => {
    try {
      await axios.put(`${API}/api/super-admin/businesses/${businessId}/status`, { status }, { headers: headers() });
      fetchBusinesses(businessesMeta.page, searchQuery);
    } catch (err) { console.error('Update business status error:', err.message); }
  };

  const handleLogout = () => {
    localStorage.removeItem('super-admin-token');
    localStorage.removeItem('super-admin-user');
    navigate('/super-admin');
  };

  const rankEmojis = { mercure: '🪨', venus: '🌕', terre: '🌍', mars: '🔴', jupiter: '🟠', saturne: '🪐' };
  const rankLabels = {
    mercure: { fr: 'Mercure', en: 'Mercury' }, venus: { fr: 'Vénus', en: 'Venus' },
    terre: { fr: 'Terre', en: 'Earth' }, mars: { fr: 'Mars', en: 'Mars' },
    jupiter: { fr: 'Jupiter', en: 'Jupiter' }, saturne: { fr: 'Saturne', en: 'Saturn' }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#0a0a0f' }}>
        <div className="w-12 h-12 border-4 border-purple-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const tabs = [
    { id: 'overview', icon: BarChart3, label: t('Vue d\'ensemble', 'Overview') },
    { id: 'members', icon: Users, label: t('Membres', 'Members') },
    { id: 'businesses', icon: Building2, label: t('Entreprises', 'Businesses') },
    { id: 'listings', icon: Megaphone, label: t('Annonces', 'Listings') },
  ];

  return (
    <div className="min-h-screen" style={{ background: '#0a0a0f' }} data-testid="super-admin-dashboard">
      {/* Top Bar */}
      <header className="sticky top-0 z-50 bg-[#0a0a0f]/90 backdrop-blur-xl border-b border-white/5 px-4 md:px-6 py-3">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-gradient-to-br from-purple-600 to-indigo-700 rounded-xl flex items-center justify-center shadow-lg shadow-purple-500/20">
              <Shield className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="font-bold text-white text-sm">Super Admin</h1>
              <p className="text-[10px] text-white/40">{adminUser?.email}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/dashboard" className="text-xs text-white/40 hover:text-white/70 transition-colors flex items-center gap-1">
              <MapPin className="w-3 h-3" /> LocalSite.Club
            </Link>
            <button onClick={handleLogout} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/5 text-white/60 hover:text-red-400 hover:bg-red-500/10 transition-all text-xs"
              data-testid="sa-logout-btn">
              <LogOut className="w-3.5 h-3.5" /> {t('Déconnexion', 'Logout')}
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 md:px-6 py-6">
        {/* Tabs */}
        <div className="flex gap-1 mb-6 bg-white/5 rounded-xl p-1 overflow-x-auto" data-testid="sa-tabs">
          {tabs.map(tab => (
            <button key={tab.id} onClick={() => { setActiveTab(tab.id); setSearchQuery(''); }}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium whitespace-nowrap transition-all ${
                activeTab === tab.id
                  ? 'bg-gradient-to-r from-purple-600 to-indigo-600 text-white shadow-lg shadow-purple-500/20'
                  : 'text-white/50 hover:text-white/80 hover:bg-white/5'
              }`}
              data-testid={`sa-tab-${tab.id}`}>
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Search Bar (for tabs other than overview) */}
        {activeTab !== 'overview' && (
          <div className="flex gap-2 mb-6">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
              <input
                value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleSearch()}
                placeholder={t('Rechercher...', 'Search...')}
                className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-white/10 bg-white/5 text-white placeholder-white/30 text-sm outline-none focus:border-purple-500/50"
                data-testid="sa-search-input"
              />
            </div>
            <button onClick={handleSearch}
              className="px-4 py-2.5 rounded-xl bg-purple-600 text-white text-sm font-medium hover:bg-purple-500 transition-colors"
              data-testid="sa-search-btn">
              {t('Chercher', 'Search')}
            </button>
          </div>
        )}

        {/* Overview Tab */}
        {activeTab === 'overview' && stats && (
          <div className="space-y-6" data-testid="sa-overview">
            {/* Stat Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[
                { label: t('Membres total', 'Total Members'), value: stats.members.total, icon: Users, color: 'purple' },
                { label: t('Entreprises', 'Businesses'), value: stats.businesses.total, icon: Building2, color: 'blue' },
                { label: t('Annonces', 'Listings'), value: stats.listings.total, icon: Megaphone, color: 'emerald' },
                { label: t('Commissions payées', 'Commissions Paid'), value: `$${stats.finances.total_commissions_paid.toLocaleString()}`, icon: DollarSign, color: 'amber' },
              ].map((card, i) => (
                <div key={i} className="bg-white/5 border border-white/10 rounded-2xl p-5">
                  <div className="flex items-center gap-3 mb-3">
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center bg-${card.color}-500/10`}>
                      <card.icon className={`w-5 h-5 text-${card.color}-400`} />
                    </div>
                  </div>
                  <p className="text-2xl font-bold text-white">{card.value}</p>
                  <p className="text-xs text-white/50 mt-1">{card.label}</p>
                </div>
              ))}
            </div>

            {/* Secondary Stats */}
            <div className="grid md:grid-cols-3 gap-4">
              <div className="bg-white/5 border border-white/10 rounded-2xl p-5">
                <h3 className="text-sm font-semibold text-white/70 mb-3 flex items-center gap-2">
                  <Activity className="w-4 h-4 text-green-400" />
                  {t('Activité', 'Activity')}
                </h3>
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-white/50">{t('Actifs', 'Active')}</span>
                    <span className="text-green-400 font-medium">{stats.members.active}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-white/50">{t('Inactifs', 'Inactive')}</span>
                    <span className="text-red-400 font-medium">{stats.members.inactive}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-white/50">{t('Affiliés', 'Affiliates')}</span>
                    <span className="text-purple-400 font-medium">{stats.members.affiliates}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-white/50">{t('Nouveaux ce mois', 'New This Month')}</span>
                    <span className="text-blue-400 font-medium">{stats.members.new_this_month}</span>
                  </div>
                </div>
              </div>

              <div className="bg-white/5 border border-white/10 rounded-2xl p-5">
                <h3 className="text-sm font-semibold text-white/70 mb-3 flex items-center gap-2">
                  <Crown className="w-4 h-4 text-amber-400" />
                  {t('Par rang', 'By Rank')}
                </h3>
                <div className="space-y-2">
                  {Object.entries(stats.members.by_rank || {}).map(([rank, count]) => (
                    <div key={rank} className="flex justify-between text-sm">
                      <span className="text-white/50">{rankEmojis[rank]} {rankLabels[rank]?.[language] || rank}</span>
                      <span className="text-white/80 font-medium">{count}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-white/5 border border-white/10 rounded-2xl p-5">
                <h3 className="text-sm font-semibold text-white/70 mb-3 flex items-center gap-2">
                  <DollarSign className="w-4 h-4 text-green-400" />
                  {t('Finances', 'Finances')}
                </h3>
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-white/50">{t('Solde total wallets', 'Total Wallet Balance')}</span>
                    <span className="text-white/80 font-medium">${stats.finances.total_wallet_balance.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-white/50">{t('Gains matrice', 'Matrix Earnings')}</span>
                    <span className="text-white/80 font-medium">${stats.finances.total_matrix_earnings.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-white/50">{t('Entr. en attente', 'Pending Businesses')}</span>
                    <span className="text-amber-400 font-medium">{stats.businesses.pending}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Members Tab */}
        {activeTab === 'members' && (
          <div data-testid="sa-members-tab">
            <div className="bg-white/5 border border-white/10 rounded-2xl overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-white/10">
                      <th className="text-left px-4 py-3 text-white/50 font-medium">{t('Nom', 'Name')}</th>
                      <th className="text-left px-4 py-3 text-white/50 font-medium hidden md:table-cell">{t('Courriel', 'Email')}</th>
                      <th className="text-left px-4 py-3 text-white/50 font-medium">{t('Rang', 'Rank')}</th>
                      <th className="text-left px-4 py-3 text-white/50 font-medium hidden lg:table-cell">{t('Rôle', 'Role')}</th>
                      <th className="text-right px-4 py-3 text-white/50 font-medium hidden lg:table-cell">{t('Solde', 'Balance')}</th>
                      <th className="text-right px-4 py-3 text-white/50 font-medium">{t('Actions', 'Actions')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {members.map(m => (
                      <tr key={m.user_id} className="border-b border-white/5 hover:bg-white/5 transition-colors">
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <div className="w-8 h-8 rounded-full bg-purple-500/20 flex items-center justify-center text-purple-400 text-xs font-bold">
                              {m.name?.charAt(0)?.toUpperCase() || '?'}
                            </div>
                            <div>
                              <p className="text-white font-medium text-sm">{m.name}</p>
                              <p className="text-white/30 text-xs md:hidden">{m.email}</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-white/60 hidden md:table-cell">{m.email}</td>
                        <td className="px-4 py-3">
                          <span className="text-sm">{rankEmojis[m.rank]} {rankLabels[m.rank]?.[language] || m.rank}</span>
                        </td>
                        <td className="px-4 py-3 hidden lg:table-cell">
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                            m.role === 'super_admin' ? 'bg-purple-500/20 text-purple-400' :
                            m.role === 'eco_region' ? 'bg-blue-500/20 text-blue-400' :
                            'bg-white/10 text-white/50'
                          }`}>
                            {m.role || 'member'}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-right text-white/60 hidden lg:table-cell">
                          ${(m.wallet_balance || 0).toFixed(2)}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center justify-end gap-1">
                            <button onClick={() => setEditModal(m)} title={t('Modifier', 'Edit')}
                              className="p-1.5 rounded-lg hover:bg-white/10 text-white/40 hover:text-white transition-colors"
                              data-testid={`sa-edit-member-${m.user_id}`}>
                              <Edit className="w-3.5 h-3.5" />
                            </button>
                            <button onClick={() => handleDeleteMember(m.user_id, m.name)} title={t('Supprimer', 'Delete')}
                              className="p-1.5 rounded-lg hover:bg-red-500/10 text-white/40 hover:text-red-400 transition-colors"
                              data-testid={`sa-delete-member-${m.user_id}`}>
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                    {members.length === 0 && (
                      <tr><td colSpan={6} className="text-center py-8 text-white/30">{t('Aucun membre trouvé', 'No members found')}</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
              {/* Pagination */}
              <Pagination meta={membersMeta} onPage={(p) => fetchMembers(p, searchQuery)} t={t} />
            </div>
          </div>
        )}

        {/* Businesses Tab */}
        {activeTab === 'businesses' && (
          <div data-testid="sa-businesses-tab">
            <div className="bg-white/5 border border-white/10 rounded-2xl overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-white/10">
                      <th className="text-left px-4 py-3 text-white/50 font-medium">{t('Nom', 'Name')}</th>
                      <th className="text-left px-4 py-3 text-white/50 font-medium hidden md:table-cell">{t('Ville', 'City')}</th>
                      <th className="text-left px-4 py-3 text-white/50 font-medium">{t('Statut', 'Status')}</th>
                      <th className="text-right px-4 py-3 text-white/50 font-medium">{t('Actions', 'Actions')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {businesses.map(b => (
                      <tr key={b.business_id} className="border-b border-white/5 hover:bg-white/5 transition-colors">
                        <td className="px-4 py-3">
                          <p className="text-white font-medium text-sm">{b.name}</p>
                          <p className="text-white/30 text-xs">{b.category}</p>
                        </td>
                        <td className="px-4 py-3 text-white/60 hidden md:table-cell">{b.city}</td>
                        <td className="px-4 py-3">
                          <StatusBadge status={b.status || 'approved'} />
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center justify-end gap-1">
                            {b.status === 'pending' && (
                              <button onClick={() => handleUpdateBusinessStatus(b.business_id, 'approved')}
                                className="p-1.5 rounded-lg hover:bg-green-500/10 text-white/40 hover:text-green-400 transition-colors" title={t('Approuver', 'Approve')}>
                                <Check className="w-3.5 h-3.5" />
                              </button>
                            )}
                            <button onClick={() => handleDeleteBusiness(b.business_id)}
                              className="p-1.5 rounded-lg hover:bg-red-500/10 text-white/40 hover:text-red-400 transition-colors" title={t('Supprimer', 'Delete')}>
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                    {businesses.length === 0 && (
                      <tr><td colSpan={4} className="text-center py-8 text-white/30">{t('Aucune entreprise trouvée', 'No businesses found')}</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
              <Pagination meta={businessesMeta} onPage={(p) => fetchBusinesses(p, searchQuery)} t={t} />
            </div>
          </div>
        )}

        {/* Listings Tab */}
        {activeTab === 'listings' && (
          <div data-testid="sa-listings-tab">
            <div className="bg-white/5 border border-white/10 rounded-2xl overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-white/10">
                      <th className="text-left px-4 py-3 text-white/50 font-medium">{t('Titre', 'Title')}</th>
                      <th className="text-left px-4 py-3 text-white/50 font-medium hidden md:table-cell">{t('Auteur', 'Author')}</th>
                      <th className="text-left px-4 py-3 text-white/50 font-medium hidden md:table-cell">{t('Type', 'Type')}</th>
                      <th className="text-right px-4 py-3 text-white/50 font-medium">{t('Actions', 'Actions')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {listings.map(l => (
                      <tr key={l.listing_id} className="border-b border-white/5 hover:bg-white/5 transition-colors">
                        <td className="px-4 py-3">
                          <p className="text-white font-medium text-sm">{l.title}</p>
                          <p className="text-white/30 text-xs">{l.city}</p>
                        </td>
                        <td className="px-4 py-3 text-white/60 hidden md:table-cell">{l.user_name}</td>
                        <td className="px-4 py-3 hidden md:table-cell">
                          <span className="px-2 py-0.5 rounded-full bg-white/10 text-white/60 text-xs">{l.type}</span>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center justify-end gap-1">
                            <button onClick={() => handleDeleteListing(l.listing_id)}
                              className="p-1.5 rounded-lg hover:bg-red-500/10 text-white/40 hover:text-red-400 transition-colors" title={t('Supprimer', 'Delete')}>
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                    {listings.length === 0 && (
                      <tr><td colSpan={4} className="text-center py-8 text-white/30">{t('Aucune annonce trouvée', 'No listings found')}</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
              <Pagination meta={listingsMeta} onPage={(p) => fetchListings(p, searchQuery)} t={t} />
            </div>
          </div>
        )}
      </div>

      {/* Edit Member Modal */}
      {editModal && (
        <EditMemberModal
          member={editModal}
          onClose={() => setEditModal(null)}
          onSave={handleUpdateMember}
          t={t}
          language={language}
          rankLabels={rankLabels}
        />
      )}
    </div>
  );
};

// ==== Sub-components ====

const Pagination = ({ meta, onPage, t }) => (
  <div className="flex items-center justify-between px-4 py-3 border-t border-white/5">
    <p className="text-xs text-white/40">
      {t(`${meta.total} résultats — Page ${meta.page}/${meta.pages}`, `${meta.total} results — Page ${meta.page}/${meta.pages}`)}
    </p>
    <div className="flex gap-1">
      <button onClick={() => onPage(meta.page - 1)} disabled={meta.page <= 1}
        className="p-1.5 rounded-lg hover:bg-white/10 text-white/40 disabled:opacity-30 transition-colors">
        <ChevronLeft className="w-4 h-4" />
      </button>
      <button onClick={() => onPage(meta.page + 1)} disabled={meta.page >= meta.pages}
        className="p-1.5 rounded-lg hover:bg-white/10 text-white/40 disabled:opacity-30 transition-colors">
        <ChevronRight className="w-4 h-4" />
      </button>
    </div>
  </div>
);

const StatusBadge = ({ status }) => {
  const colors = {
    approved: 'bg-green-500/20 text-green-400',
    pending: 'bg-amber-500/20 text-amber-400',
    rejected: 'bg-red-500/20 text-red-400',
    suspended: 'bg-gray-500/20 text-gray-400'
  };
  return <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${colors[status] || colors.approved}`}>{status}</span>;
};

const EditMemberModal = ({ member, onClose, onSave, t, language, rankLabels }) => {
  const [form, setForm] = useState({
    name: member.name || '',
    role: member.role || 'member',
    rank: member.rank || 'mercure',
    is_active: member.is_active !== false
  });

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm px-4" data-testid="sa-edit-modal">
      <div className="bg-[#1a1a2e] border border-white/10 rounded-2xl w-full max-w-md p-6">
        <div className="flex items-center justify-between mb-5">
          <h3 className="text-white font-bold">{t('Modifier le membre', 'Edit Member')}</h3>
          <button onClick={onClose} className="text-white/40 hover:text-white"><X className="w-5 h-5" /></button>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-xs text-white/60 mb-1">{t('Nom', 'Name')}</label>
            <input value={form.name} onChange={e => setForm({...form, name: e.target.value})}
              className="w-full px-3 py-2 rounded-lg border border-white/10 bg-white/5 text-white text-sm outline-none focus:border-purple-500/50"
              data-testid="sa-edit-name" />
          </div>

          <div>
            <label className="block text-xs text-white/60 mb-1">{t('Rôle', 'Role')}</label>
            <select value={form.role} onChange={e => setForm({...form, role: e.target.value})}
              className="w-full px-3 py-2 rounded-lg border border-white/10 bg-white/5 text-white text-sm outline-none focus:border-purple-500/50"
              data-testid="sa-edit-role">
              <option value="member">{t('Membre', 'Member')}</option>
              <option value="admin">Admin</option>
              <option value="eco_region">EcoRegion</option>
              <option value="super_admin">Super Admin</option>
            </select>
          </div>

          <div>
            <label className="block text-xs text-white/60 mb-1">{t('Rang', 'Rank')}</label>
            <select value={form.rank} onChange={e => setForm({...form, rank: e.target.value})}
              className="w-full px-3 py-2 rounded-lg border border-white/10 bg-white/5 text-white text-sm outline-none focus:border-purple-500/50"
              data-testid="sa-edit-rank">
              {['mercure', 'venus', 'terre', 'mars', 'jupiter', 'saturne'].map(r => (
                <option key={r} value={r}>{rankLabels[r]?.[language] || r}</option>
              ))}
            </select>
          </div>

          <div className="flex items-center gap-2">
            <input type="checkbox" checked={form.is_active} onChange={e => setForm({...form, is_active: e.target.checked})}
              className="rounded border-white/20" id="is_active" data-testid="sa-edit-active" />
            <label htmlFor="is_active" className="text-sm text-white/60">{t('Actif', 'Active')}</label>
          </div>
        </div>

        <div className="flex gap-2 mt-6">
          <button onClick={onClose} className="flex-1 py-2.5 rounded-xl border border-white/10 text-white/60 text-sm hover:bg-white/5 transition-colors">
            {t('Annuler', 'Cancel')}
          </button>
          <button onClick={() => onSave(member.user_id, form)}
            className="flex-1 py-2.5 rounded-xl bg-purple-600 text-white text-sm font-medium hover:bg-purple-500 transition-colors"
            data-testid="sa-edit-save">
            {t('Sauvegarder', 'Save')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SuperAdminDashboard;
